import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, throwError, of } from 'rxjs';
import { catchError, tap, switchMap, map } from 'rxjs/operators';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Login } from '../models/login';
import { environment } from 'src/environments/environment.dev';
import { Signup } from '../models/signup';
import { decodeToken } from '../utilities/login-utils';
import { Profile } from '../models/profile';

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    private url: string = environment.apiUrl;
    private jwtHelper = new JwtHelperService();
    private refreshInProgress = false;
    private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject(null);

    constructor(private http: HttpClient) {}

    login(loginPayload: Login): Observable<any> {
        return this.http
            .post<any>(`/auth/admin/login`, loginPayload)
            .pipe(tap((response) => this.storeTokens(response)));
    }


  logout() {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('profile');
  }

    signup(userData: Signup): Observable<any> {
        return this.http.post(`/enrolment/signup`, userData);
    }

    resetPassword(body: any): Observable<any> {
        return this.http.post(`/enrolment/forgot-password`, body);
    }

    storeTokens(response: any) {
        localStorage.setItem('access_token', response.access_token);
        localStorage.setItem('refresh_token', response.refresh_token);
        localStorage.setItem('profile', JSON.stringify(decodeToken(response['access_token'])));
    }

    getAccessToken(): string | null {
        return localStorage.getItem('access_token');
    }

    getProfile(): Profile | null {
      const profile = localStorage.getItem('profile');
      return profile ? JSON.parse(profile) : null;
    }


    getRefreshToken(): string | null {
      return localStorage.getItem('refresh_token');
    }

    isTokenExpired(): boolean {
      const token = this.getAccessToken();
      return token ? this.jwtHelper.isTokenExpired(token) : true;
    }

    refreshToken(): Observable<any> {
      if (this.refreshInProgress) {
        return this.refreshTokenSubject.asObservable();
      }

      this.refreshInProgress = true;
      return this.http.post<any>(`/auth/refresh-token`, {
        refreshToken: this.getRefreshToken(),
      }).pipe(
        tap((response) => {
          this.storeTokens(response);
          this.refreshInProgress = false;
          this.refreshTokenSubject.next(response.accessToken);
        }),
        catchError(error => {
          this.refreshInProgress = false;
          this.logout();
          return throwError(() => new Error('Session expired. Please login again.'));
        })
      );
    }

    ensureTokenIsValid(): Observable<boolean> {
        if (!this.isTokenExpired()) {
            return of(true);
        } else {
            return this.refreshToken().pipe(
                map(newToken => !!newToken),
                catchError(() => of(false))
            );
        }
    }
  }
